import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import {
  Container,
  Table,
  Loader,
  Segment,
  Header,
  Select,
  Pagination,
  Label,
  Input,
  Icon,
  Button,
  Breadcrumb,
  Message,
  Popup,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../api';

const PAGE_SIZE = 50;

const severityColor = (sev) => {
  switch (sev) {
    case 'critical': return 'red';
    case 'high': return 'orange';
    case 'medium': return 'yellow';
    case 'low': return 'grey';
    default: return 'grey';
  }
};

const statusColor = (st) => {
  switch (st) {
    case 'triaged': return 'orange';
    case 'fixed_local': return 'green';
    case 'reported': return 'blue';
    case 'wontfix': return 'grey';
    case 'open': return 'red';
    case 'fixed_upstream': return 'teal';
    case 'acknowledged': return 'purple';
    default: return 'grey';
  }
};

const riskColor = (code) => {
  switch (code) {
    case 'S4': return 'red';
    case 'S3': return 'orange';
    case 'S2': return 'yellow';
    case 'S1': return 'grey';
    default: return null;
  }
};

const RISK_LABELS = {
  S4: 'S4 (Critical)',
  S3: 'S3 (Serious)',
  S2: 'S2 (Minor)',
  S1: 'S1 (Cosmetic)',
};

const DISPOSITION_DEFS = [
  {
    key: 'acceptable',
    icon: 'check',
    color: 'green',
    label: 'Acceptable',
    short: 'Translation is correct despite the finding — no action needed.',
    detail:
      'Use when the AI reviewer flagged something that is actually fine. ' +
      'For example, a loanword that looks like an untranslated string, ' +
      'or a cultural adaptation that the reviewer misidentified as an error. ' +
      'The finding will be marked as resolved (won\'t fix) and hidden from the default view.',
  },
  {
    key: 'confirmed',
    icon: 'exclamation triangle',
    color: 'orange',
    label: 'Issue Confirmed',
    short: 'The finding is a real issue — needs fixing.',
    detail:
      'Use when the translation genuinely has the problem described. ' +
      'The finding stays visible and can be fixed using Apply Fix ' +
      '(with the suggested fix or an edited version). ' +
      'The finding remains pending until a fix is applied.',
  },
  {
    key: 'false_positive',
    icon: 'ban',
    color: 'grey',
    label: 'False Positive',
    short: 'The finding is wrong — the reviewer made an error.',
    detail:
      'Use when the AI reviewer\'s analysis is incorrect. ' +
      'For example, it flagged a plural form as wrong when it\'s actually correct, ' +
      'or identified a "missing translation" that is intentionally blank. ' +
      'The finding will be marked as resolved (won\'t fix) and hidden from the default view.',
  },
];

const QCFindingsBrowser = () => {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const initialPlatform = searchParams.get('platform') || '';
  const initialLanguage = searchParams.get('language') || '';
  const initialStatus = searchParams.get('status') || '';
  const initialErrorType = searchParams.get('error_type') || '';
  const initialRisk = searchParams.get('risk') || '';
  const initialSearch = searchParams.get('search') || '';
  const initialMessageId = searchParams.get('message_id') || '';
  const initialPage = parseInt(searchParams.get('page'), 10) || 1;

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [facets, setFacets] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [platform, setPlatform] = useState(initialPlatform);
  const [language, setLanguage] = useState(initialLanguage);
  const [status, setStatus] = useState(initialStatus);
  const [errorType, setErrorType] = useState(initialErrorType);
  const [risk, setRisk] = useState(initialRisk);
  const [search, setSearch] = useState(initialSearch);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [messageId, setMessageId] = useState(initialMessageId);
  const [page, setPage] = useState(initialPage);

  const [updatingId, setUpdatingId] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [editedFixes, setEditedFixes] = useState({});

  const hasFilters = platform || language || status || errorType || risk || search || messageId;

  const clearAllFilters = () => {
    setPlatform('');
    setLanguage('');
    setStatus('');
    setErrorType('');
    setRisk('');
    setSearch('');
    setSearchInput('');
    setMessageId('');
    setPage(1);
  };

  const setDisposition = async (findingId, disposition) => {
    setUpdatingId(findingId);
    setActionError(null);
    try {
      await fetchApi(
        `${getApiPath()}/@polyglot/qc-findings/${findingId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ disposition }),
        },
      );
      // Resolved dispositions remove from default view
      if (disposition === 'acceptable' || disposition === 'false_positive') {
        setItems((prev) => prev.filter((item) => item.id !== findingId));
        setTotal((prev) => prev - 1);
        const def = DISPOSITION_DEFS.find((d) => d.key === disposition);
        setActionSuccess(`Finding dismissed as "${def?.label || disposition}"`);
        setTimeout(() => setActionSuccess(null), 3000);
      } else {
        setItems((prev) =>
          prev.map((item) =>
            item.id === findingId ? { ...item, disposition } : item,
          ),
        );
      }
    } catch (err) {
      setActionError(`Failed to update finding #${findingId}: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  const applyFix = async (findingId) => {
    setApplyingId(findingId);
    setActionError(null);
    try {
      const customFix = editedFixes[findingId];
      const body = customFix ? { custom_fix: customFix } : {};
      const result = await fetchApi(
        `${getApiPath()}/@polyglot/qc-findings/${findingId}/apply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      // Remove from default view (fixed items are excluded)
      if (!status) {
        setItems((prev) => prev.filter((item) => item.id !== findingId));
        setTotal((prev) => prev - 1);
      } else {
        setItems((prev) =>
          prev.map((item) =>
            item.id === findingId
              ? {
                  ...item,
                  status: 'fixed_local',
                  status_label: 'Fixed (Local)',
                  disposition: item.disposition || 'confirmed',
                  current_value: result.new_value,
                  correction_id: result.correction_id,
                }
              : item,
          ),
        );
      }
      setActionSuccess(`Fix applied to finding #${findingId}`);
      setTimeout(() => setActionSuccess(null), 3000);
    } catch (err) {
      setActionError(`Failed to apply fix for #${findingId}: ${err.message}`);
    } finally {
      setApplyingId(null);
    }
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (platform) params.set('platform', platform);
    if (language) params.set('language', language);
    if (status) params.set('status', status);
    if (errorType) params.set('error_type', errorType);
    if (risk) params.set('risk', risk);
    if (search) params.set('search', search);
    if (messageId) params.set('message_id', messageId);
    params.set('page', page);
    params.set('page_size', PAGE_SIZE);

    fetchApi(`${getApiPath()}/@polyglot/qc-findings?${params.toString()}`)
      .then((json) => {
        setItems(json.items || []);
        setTotal(json.items_total || 0);
        setFacets(json.facets || {});
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [platform, language, status, errorType, risk, search, messageId, page]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Sync state to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (platform) params.set('platform', platform);
    if (language) params.set('language', language);
    if (status) params.set('status', status);
    if (errorType) params.set('error_type', errorType);
    if (risk) params.set('risk', risk);
    if (search) params.set('search', search);
    if (messageId) params.set('message_id', messageId);
    if (page > 1) params.set('page', String(page));
    const qs = params.toString();
    history.replace(`/polyglot/qc/findings${qs ? `?${qs}` : ''}`);
  }, [platform, language, status, errorType, risk, search, messageId, page, history]);

  // Reset expanded rows on page/filter change
  useEffect(() => {
    setExpandedRows(new Set());
    setEditedFixes({});
  }, [platform, language, status, errorType, risk, search, messageId, page]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const makeOptions = (facetList, allLabel) => [
    { key: '_all', value: '', text: allLabel },
    ...(facetList || []).map((f) => ({
      key: f.value,
      value: f.value,
      text: `${f.label || f.value} (${f.count})`,
    })),
  ];

  const makeRiskOptions = (facetList) => [
    { key: '_all', value: '', text: 'All Risk Levels' },
    ...(facetList || []).map((f) => ({
      key: f.value,
      value: f.value,
      text: `${RISK_LABELS[f.value] || f.value} (${f.count})`,
    })),
  ];

  // Build active filter labels for chips
  const activeFilters = [];
  if (platform) {
    activeFilters.push({
      key: 'platform', label: `Platform: ${platform}`,
      onClear: () => { setPlatform(''); setPage(1); },
    });
  }
  if (language) {
    activeFilters.push({
      key: 'language', label: `Language: ${language}`,
      onClear: () => { setLanguage(''); setPage(1); },
    });
  }
  if (status) {
    const statusFacet = (facets.statuses || []).find((f) => f.value === status);
    activeFilters.push({
      key: 'status', label: `Status: ${statusFacet?.label || status}`,
      onClear: () => { setStatus(''); setPage(1); },
    });
  }
  if (errorType) {
    const etFacet = (facets.error_types || []).find((f) => f.value === errorType);
    activeFilters.push({
      key: 'error_type', label: `Error: ${etFacet?.label || errorType}`,
      onClear: () => { setErrorType(''); setPage(1); },
    });
  }
  if (risk) {
    activeFilters.push({
      key: 'risk', label: `Risk: ${RISK_LABELS[risk] || risk}`,
      onClear: () => { setRisk(''); setPage(1); },
    });
  }
  if (search) {
    activeFilters.push({
      key: 'search', label: `Search: "${search}"`,
      onClear: () => { setSearch(''); setSearchInput(''); setPage(1); },
    });
  }
  if (messageId) {
    activeFilters.push({
      key: 'message_id', label: `Message: #${messageId}`,
      onClear: () => { setMessageId(''); setPage(1); },
    });
  }

  return (
    <Container
      style={{ paddingTop: '2em', paddingBottom: '2em', maxWidth: '1400px' }}
      id="qc-findings-browser"
    >
      <Helmet title="QC Findings Browser" />

      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: '1em' }}>
        <Breadcrumb.Section as={Link} to="/polyglot">Polyglot</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section as={Link} to="/polyglot/qc">QC Dashboard</Breadcrumb.Section>
        <Breadcrumb.Divider icon="right angle" />
        <Breadcrumb.Section active>Findings</Breadcrumb.Section>
      </Breadcrumb>

      <div className="polyglot-page-header">
        <h1>QC Findings</h1>
        <p className="subtitle">
          {total.toLocaleString()} findings
          {platform ? ` \u2014 ${platform}` : ''}
          {language ? ` \u2014 ${language}` : ''}
          {messageId ? ` \u2014 message #${messageId}` : ''}
        </p>
      </div>

      {/* Inline messages */}
      {actionError && (
        <Message negative onDismiss={() => setActionError(null)}>
          <Message.Header>Error</Message.Header>
          <p>{actionError}</p>
        </Message>
      )}
      {actionSuccess && (
        <Message positive onDismiss={() => setActionSuccess(null)} size="small">
          <Icon name="check circle" /> {actionSuccess}
        </Message>
      )}

      {/* Filters */}
      <Segment>
        <div
          style={{
            display: 'flex',
            gap: '0.8em',
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Select
            placeholder="Platform"
            options={makeOptions(facets.platforms, 'All Platforms')}
            value={platform}
            onChange={(e, { value }) => { setPlatform(value); setPage(1); }}
            style={{ minWidth: '140px' }}
          />
          <Select
            placeholder="Language"
            options={makeOptions(facets.languages, 'All Languages')}
            value={language}
            onChange={(e, { value }) => { setLanguage(value); setPage(1); }}
            search
            style={{ minWidth: '140px' }}
          />
          <Select
            placeholder="Status"
            options={makeOptions(facets.statuses, 'All Statuses')}
            value={status}
            onChange={(e, { value }) => { setStatus(value); setPage(1); }}
            style={{ minWidth: '140px' }}
          />
          <Select
            placeholder="Error Type"
            options={makeOptions(facets.error_types, 'All Error Types')}
            value={errorType}
            onChange={(e, { value }) => { setErrorType(value); setPage(1); }}
            style={{ minWidth: '160px' }}
          />
          <Select
            placeholder="Risk"
            options={makeRiskOptions(facets.risk_severities)}
            value={risk}
            onChange={(e, { value }) => { setRisk(value); setPage(1); }}
            style={{ minWidth: '120px' }}
          />
          <Input
            placeholder="Search..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { setSearch(searchInput); setPage(1); }
            }}
            action={{
              icon: 'search',
              onClick: () => { setSearch(searchInput); setPage(1); },
            }}
            style={{ minWidth: '200px' }}
          />
          {hasFilters && (
            <Button
              basic
              size="small"
              onClick={clearAllFilters}
              style={{ marginLeft: 'auto' }}
            >
              <Icon name="times" /> Clear All
            </Button>
          )}
        </div>

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <div style={{ marginTop: '0.8em', display: 'flex', gap: '0.4em', flexWrap: 'wrap' }}>
            {activeFilters.map((f) => (
              <Label key={f.key} size="small" color="blue">
                {f.label}
                <Icon name="delete" onClick={f.onClear} style={{ cursor: 'pointer' }} />
              </Label>
            ))}
          </div>
        )}
      </Segment>

      {error && <Segment color="red">Error: {error}</Segment>}

      {loading ? (
        <Loader active inline="centered" content="Loading findings..." />
      ) : (
        <>
          <Table celled selectable unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ width: '40px' }} />
                <Table.HeaderCell style={{ width: '55px' }}>Lang</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '70px' }}>Platform</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '60px' }}>Msg ID</Table.HeaderCell>
                <Table.HeaderCell>English Source</Table.HeaderCell>
                <Table.HeaderCell>Current Value</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '120px' }}>Error Type</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '80px' }}>Severity</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '55px' }}>Risk</Table.HeaderCell>
                <Table.HeaderCell style={{ width: '90px' }}>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {items.map((row) => {
                const isExpanded = expandedRows.has(row.id);
                return (
                  <React.Fragment key={row.id}>
                    <Table.Row
                      onClick={() => toggleRow(row.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <Table.Cell textAlign="center" style={{ padding: '0.5em 0' }}>
                        <Icon
                          name={isExpanded ? 'chevron down' : 'chevron right'}
                          style={{ color: '#888', margin: 0 }}
                        />
                      </Table.Cell>
                      <Table.Cell><strong>{row.language}</strong></Table.Cell>
                      <Table.Cell style={{ color: '#666' }}>{row.platform}</Table.Cell>
                      <Table.Cell>
                        <Link
                          to={`/polyglot/messages/${row.message_id}?language=${row.language}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.message_id}
                        </Link>
                      </Table.Cell>
                      <Table.Cell style={{ wordBreak: 'break-word' }}>
                        {row.english_source}
                      </Table.Cell>
                      <Table.Cell style={{ wordBreak: 'break-word' }}>
                        {row.live_value ?? row.current_value}
                      </Table.Cell>
                      <Table.Cell>
                        <Label size="small">
                          {row.error_type_label || row.error_type}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        <Label size="small" color={severityColor(row.severity)}>
                          {row.severity_label || row.severity}
                        </Label>
                      </Table.Cell>
                      <Table.Cell>
                        {row.risk_severity ? (
                          <Label size="small" color={riskColor(row.risk_severity)}>
                            {row.risk_severity}
                          </Label>
                        ) : (
                          <span style={{ color: '#ccc' }}>&mdash;</span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <Label size="small" color={statusColor(row.status)}>
                          {row.status_label || row.status}
                        </Label>
                      </Table.Cell>
                    </Table.Row>

                    {isExpanded && (
                      <Table.Row>
                        <Table.Cell colSpan="10" style={{ background: '#f8f9fa' }}>
                          <ExpandedDetail
                            row={row}
                            updatingId={updatingId}
                            applyingId={applyingId}
                            editedFixes={editedFixes}
                            setEditedFixes={setEditedFixes}
                            setDisposition={setDisposition}
                            applyFix={applyFix}
                          />
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </React.Fragment>
                );
              })}
              {items.length === 0 && (
                <Table.Row>
                  <Table.Cell colSpan="10" textAlign="center">
                    <Segment placeholder>
                      <Header icon>
                        <Icon name="search" />
                        No findings match the current filters
                      </Header>
                      {hasFilters && (
                        <Button primary onClick={clearAllFilters}>
                          Clear All Filters
                        </Button>
                      )}
                    </Segment>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>

          {totalPages > 1 && (
            <div style={{ textAlign: 'center', marginTop: '1.5em' }}>
              <Pagination
                activePage={page}
                totalPages={totalPages}
                onPageChange={(e, { activePage }) => setPage(activePage)}
                siblingRange={2}
              />
            </div>
          )}
        </>
      )}
    </Container>
  );
};

/** Expanded detail panel for a single finding row */
const ExpandedDetail = ({
  row,
  updatingId,
  applyingId,
  editedFixes,
  setEditedFixes,
  setDisposition,
  applyFix,
}) => {
  const isEditing = editedFixes[row.id] !== undefined;
  const editValue = editedFixes[row.id] ?? '';

  return (
    <div style={{ padding: '0.8em 1em' }}>
      {/* Finding description */}
      <div style={{ marginBottom: '0.8em' }}>
        <SectionLabel>Finding</SectionLabel>
        <div style={{ fontSize: '1.05em', lineHeight: '1.5' }}>
          {row.finding}
        </div>
      </div>

      {/* Suggested fix with edit capability */}
      {row.suggested_fix && (
        <div style={{ marginBottom: '0.8em' }}>
          <SectionLabel>Suggested Fix</SectionLabel>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.8em', flexWrap: 'wrap' }}>
            {row.status !== 'fixed_local' ? (
              <>
                {isEditing ? (
                  <div style={{ flex: 1, minWidth: '250px' }}>
                    <Input
                      fluid
                      value={editValue}
                      onChange={(e) =>
                        setEditedFixes((prev) => ({ ...prev, [row.id]: e.target.value }))
                      }
                      action={{
                        icon: 'undo',
                        content: 'Reset',
                        basic: true,
                        onClick: () =>
                          setEditedFixes((prev) => {
                            const next = { ...prev };
                            delete next[row.id];
                            return next;
                          }),
                      }}
                      style={{ fontSize: '1.05em' }}
                    />
                    <div style={{ fontSize: '0.8em', color: '#888', marginTop: '0.3em' }}>
                      Original: {row.suggested_fix}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: '1.1em',
                      color: '#16ab39',
                      fontWeight: '600',
                      padding: '0.4em 0.6em',
                      background: '#f0fff0',
                      borderRadius: '4px',
                      border: '1px solid #b5e8b5',
                      cursor: 'pointer',
                      flex: 1,
                      minWidth: '250px',
                    }}
                    title="Click to edit before applying"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditedFixes((prev) => ({
                        ...prev,
                        [row.id]: row.suggested_fix,
                      }));
                    }}
                  >
                    {row.suggested_fix}
                    <Icon
                      name="pencil"
                      size="small"
                      style={{ marginLeft: '0.5em', color: '#999', fontSize: '0.7em' }}
                    />
                  </div>
                )}
                <Button
                  size="small"
                  color="green"
                  loading={applyingId === row.id}
                  disabled={applyingId === row.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    applyFix(row.id);
                  }}
                >
                  <Icon name="check circle" />
                  Apply{isEditing ? ' Edited' : ''} Fix
                </Button>
              </>
            ) : (
              <Label color="green" size="small">
                <Icon name="check" /> Applied
                {row.correction_id && ` (correction #${row.correction_id})`}
              </Label>
            )}
          </div>
        </div>
      )}

      {/* Audit trail */}
      {row.disposition && (
        <div
          style={{
            borderTop: '1px solid #e8e8e8',
            paddingTop: '0.6em',
            marginTop: '0.4em',
            marginBottom: '0.4em',
            padding: '0.6em 0.8em',
            background: '#f5f5f5',
            borderRadius: '4px',
          }}
        >
          <SectionLabel>Audit Trail</SectionLabel>
          <div style={{ display: 'flex', gap: '1.5em', flexWrap: 'wrap', alignItems: 'center' }}>
            <span>
              <Icon name="user" style={{ color: '#555' }} />
              <strong>{row.disposition_by || 'unknown'}</strong>
            </span>
            <span>
              marked as <Label size="mini" color={
                row.disposition === 'acceptable' ? 'green' :
                row.disposition === 'confirmed' ? 'orange' :
                row.disposition === 'false_positive' ? 'grey' : undefined
              }>
                {row.disposition === 'acceptable' ? 'Acceptable' :
                 row.disposition === 'confirmed' ? 'Issue Confirmed' :
                 row.disposition === 'false_positive' ? 'False Positive' :
                 row.disposition}
              </Label>
            </span>
            {row.disposition_at && (
              <span style={{ color: '#888' }}>
                <Icon name="clock outline" />
                {new Date(row.disposition_at).toLocaleString()}
              </span>
            )}
            {row.correction_id && (
              <span style={{ color: '#888' }}>
                <Icon name="wrench" />
                Correction #{row.correction_id}
              </span>
            )}
            {row.resolved_at && (
              <span style={{ color: '#888' }}>
                <Icon name="check circle" />
                Resolved {new Date(row.resolved_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Metadata row */}
      <div
        style={{
          display: 'flex',
          gap: '2em',
          flexWrap: 'wrap',
          fontSize: '0.9em',
          color: '#666',
          borderTop: row.disposition ? 'none' : '1px solid #e8e8e8',
          paddingTop: row.disposition ? '0' : '0.6em',
          marginTop: row.disposition ? '0' : '0.4em',
        }}
      >
        {row.source && (
          <span><strong>Source:</strong> {row.source}</span>
        )}
        {row.translation_source && (
          <span><strong>Translation:</strong> {row.translation_source}</span>
        )}
        {row.discovered_at && (
          <span>
            <strong>Discovered:</strong>{' '}
            {new Date(row.discovered_at).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Disposition actions */}
      {row.status !== 'fixed_local' && (
        <div
          style={{
            display: 'flex',
            gap: '0.6em',
            alignItems: 'center',
            borderTop: '1px solid #e8e8e8',
            paddingTop: '0.8em',
            marginTop: '0.6em',
            flexWrap: 'wrap',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.3em' }}>
            <SectionLabel style={{ marginBottom: 0, marginRight: '0.3em' }}>
              Disposition
            </SectionLabel>
            <DispositionHelp />
          </span>

          {DISPOSITION_DEFS.map((def) => (
            <Popup
              key={def.key}
              trigger={
                <Button
                  size="small"
                  color={def.color}
                  basic={row.disposition !== def.key}
                  loading={updatingId === row.id}
                  disabled={updatingId === row.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDisposition(row.id, def.key);
                  }}
                >
                  <Icon name={def.icon} />
                  {def.label}
                </Button>
              }
              content={def.short}
              size="small"
              position="top center"
              inverted
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** Small label for sections within expanded detail */
const SectionLabel = ({ children, style }) => (
  <strong
    style={{
      display: 'block',
      marginBottom: '0.3em',
      color: '#555',
      fontSize: '0.85em',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      ...style,
    }}
  >
    {children}
  </strong>
);

/** Help popup explaining disposition options */
const DispositionHelp = () => (
  <Popup
    trigger={
      <Icon
        name="question circle outline"
        style={{ color: '#999', cursor: 'pointer', fontSize: '1em' }}
      />
    }
    wide="very"
    position="bottom left"
  >
    <Popup.Header>Disposition Options</Popup.Header>
    <Popup.Content>
      <div style={{ fontSize: '0.9em' }}>
        {DISPOSITION_DEFS.map((def) => (
          <div key={def.key} style={{ marginBottom: '0.8em' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '0.2em' }}>
              <Icon name={def.icon} color={def.color} /> {def.label}
            </div>
            <div style={{ color: '#555', lineHeight: '1.4' }}>{def.detail}</div>
          </div>
        ))}
      </div>
    </Popup.Content>
  </Popup>
);

export default QCFindingsBrowser;
